import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import i18n from 'i18next';
import * as React from 'react';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250
        }
    }
};

interface ISelectProps {
    label?: string;
    options: any[];
    multiple?: boolean;
    onChange?: (value: string[]) => void;
    displayValue?: string;
    selectIndex?: any;
    selectId?: (id: number) => void;
    hasError?: boolean;
    errorMessage?: string;
    style?: SxProps<Theme>;
    shouldClear?: boolean;
    defaultValue?: string;
    isNoAutoChosen?: boolean;
}

const MultipleSelectCheckmarks: React.FC<ISelectProps> = ({
    label,
    options = [],
    onChange,
    multiple,
    displayValue,
    selectId,
    hasError,
    errorMessage,
    style,
    shouldClear,
    defaultValue,
    isNoAutoChosen
}) => {
    const theme = useTheme();
    const displayValues = displayValue ? options?.map((item) => item[displayValue]) : options;
    const [values, setValues] = React.useState<string[]>(() =>
        defaultValue && isNoAutoChosen ? [defaultValue] : []
    );

    const handleChange = (event: SelectChangeEvent<typeof values>) => {
        const { value } = event.target;
        const newValues = typeof value === 'string' ? value.split(',') : value;
        setValues(newValues);
        onChange?.(newValues);
    };

    const handleIndexNidChange = (e: any, index: number) => {
        e.stopPropagation();
        if (Array.isArray(options) && index !== -1) {
            const selectedId = options[index]?.categoryId ?? options[index]?.id ?? -1;
            selectId?.(selectedId);
        } else {
            selectId?.(-1);
        }
    };

    const clearValues = () => {
        setValues([]);
        onChange?.([]);
        selectId?.(-1);
    };

    React.useEffect(() => {
        if (Array.isArray(displayValues)) {
            if (isNoAutoChosen && defaultValue) {
                setValues([defaultValue]);
                onChange?.([defaultValue]);
                selectId?.(-1);
            } else if (!isNoAutoChosen && displayValues.length > 0) {
                let selectedId = options[0]?.categoryId ?? options[0]?.id ?? -1;
                selectId?.(selectedId);
                setValues([displayValues[0]]);
                onChange?.([displayValues[0]]);
            }
        }
        return () => {
            clearValues();
        };
    }, [shouldClear, i18n.language, isNoAutoChosen, defaultValue]);

    if (!options.length) return null;

    return (
        <Box sx={{ my: 2, flexGrow: 1, ...style }}>
            {label && (
                <InputLabel sx={{ mb: 0.5, color: hasError ? 'error.main' : 'text.primary' }} id="multi-select-label">
                    {label} {errorMessage && <span>*</span>}
                </InputLabel>
            )}

            <FormControl fullWidth error={hasError}>
                <Select
                    labelId="multi-select-label"
                    id="multi-select"
                    multiple={multiple}
                    value={
                        (multiple
                            ? values
                            : (values[0] ?? defaultValue ?? (Array.isArray(displayValues) ? displayValues[0] : '') ?? '')) as string[] | ''
                    }
                    onChange={handleChange}
                    input={<OutlinedInput />}
                    renderValue={(selected) => (Array.isArray(selected) ? selected.join(', ') : selected ?? '')}
                    MenuProps={MenuProps}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: hasError ? theme.palette.error.main : theme.palette.mode === 'dark' ? '#fff' : '#000',
                                borderWidth: 1
                            },
                            '&:hover fieldset': {
                                borderColor: hasError ? theme.palette.error.main : theme.palette.mode === 'dark' ? '#fff' : '#000'
                            }
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: hasError ? theme.palette.error.main : theme.palette.mode === 'dark' ? '#fff' : '#000',
                            height: 58
                        }
                    }}
                    inputProps={{
                        style: {
                            color: hasError ? theme.palette.error.main : theme.palette.mode === 'dark' ? '#fff' : '#000'
                        }
                    }}
                >
                    {defaultValue && (
                        <MenuItem key={-1} value={defaultValue} onClick={(e) => handleIndexNidChange(e, -1)}>
                            {multiple && <Checkbox checked={values.indexOf(defaultValue) > -1} />}
                            <ListItemText primary={defaultValue} />
                        </MenuItem>
                    )}
                    {displayValues.map((option, index) => (
                        <MenuItem key={index} value={option} onClick={(e) => handleIndexNidChange(e, index)}>
                            {multiple && <Checkbox checked={values.indexOf(option) > -1} />}
                            <ListItemText primary={option} />
                        </MenuItem>
                    ))}
                </Select>

                {hasError && <FormHelperText>{errorMessage}</FormHelperText>}
            </FormControl>
        </Box>
    );
};

export default MultipleSelectCheckmarks;
